import React, { useState, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import axios from 'axios';
import logo from '../assets/LogoW.webp';
import Dashboard from '../pages/Dashboard';

const API = process.env.REACT_APP_API_URL || 'https://api.pixlybooth.com';

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/notifications`, { withCredentials: true })
      .then(r => setUnread(r.data.unreadCount || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!socket) return;
    const handleNotif = () => setUnread(n => n + 1);
    const handleStatus = () => setUnread(n => n + 1);
    socket.on('status_update', handleStatus);
    socket.on('contract_ready', handleNotif);
    return () => { socket.off('status_update', handleStatus); socket.off('contract_ready', handleNotif); };
  }, [socket]);

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const navItems = [
    { to: '/dashboard', label: '📋 Booking Status', end: true },
    { to: '/dashboard/contract', label: '📄 My Contract' },
    { to: '/dashboard/invoice', label: '💰 Invoice & Payment' },
    { to: '/dashboard/notifications', label: `🔔 Notifications${unread > 0 ? ` (${unread})` : ''}` },
  ];

  const sidebarStyle = {
    width: 240, background: 'linear-gradient(180deg, #101428 0%, #0D0F20 100%)',
    borderRight: '1px solid rgba(227,67,100,0.2)', display: 'flex', flexDirection: 'column',
    minHeight: '100vh', position: 'fixed', top: 0, left: 0, zIndex: 100,
    transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0B0F1F', display: 'flex', fontFamily: 'Arial, Helvetica, sans-serif' }}>
      {/* Desktop sidebar always visible */}
      <style>{`@media (min-width: 768px) { #dashboard-sidebar { transform: translateX(0) !important; } #dashboard-content { margin-left: 240px; } }`}</style>

      <div id="dashboard-sidebar" style={sidebarStyle}>
        {/* Logo */}
        <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <img src={logo} alt="PixlyBooth" style={{ height: 40 }} />
        </div>

        {/* User info */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#E34364', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 8 }}>
            {user?.name?.[0]?.toUpperCase()}
          </div>
          <p style={{ margin: 0, fontSize: 13, color: '#fff', fontWeight: 600 }}>{user?.name}</p>
          <p style={{ margin: '2px 0 0', fontSize: 11, color: '#a0a0b8' }}>{user?.email}</p>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '12px 12px' }}>
          {navItems.map(({ to, label, end }) => (
            <NavLink key={to} to={to} end={end}
              onClick={() => setMobileOpen(false)}
              style={({ isActive }) => ({
                display: 'block', padding: '10px 14px', borderRadius: 10, marginBottom: 4,
                fontSize: 13, textDecoration: 'none', fontWeight: isActive ? 700 : 400,
                color: isActive ? '#fff' : '#a0a0b8',
                background: isActive ? 'rgba(227,67,100,0.15)' : 'transparent',
                borderLeft: isActive ? '3px solid #E34364' : '3px solid transparent',
                transition: 'all 0.15s',
              })}
            >
              {label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <div style={{ padding: '12px 12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={handleLogout} style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: '10px 14px', color: '#a0a0b8', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}>
            🚪 Sign Out
          </button>
          <NavLink to="/" style={{ display: 'block', padding: '8px 14px', fontSize: 12, color: '#555', textDecoration: 'none', textAlign: 'center', marginTop: 4 }}>← Homepage</NavLink>
        </div>
      </div>

      {/* Mobile overlay */}
      {mobileOpen && <div onClick={() => setMobileOpen(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 99 }} />}

      {/* Main content */}
      <div id="dashboard-content" style={{ flex: 1, padding: '24px 20px', maxWidth: '100%' }}>
        {/* Mobile header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, padding: '0 0 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer', padding: '0 4px' }}>☰</button>
          <h1 style={{ margin: 0, fontSize: 18, color: '#fff', fontWeight: 700 }}>My Dashboard</h1>
          {unread > 0 && (
            <span style={{ marginLeft: 'auto', background: '#E34364', color: '#fff', fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 50 }}>
              {unread} new
            </span>
          )}
        </div>

        <Routes>
          <Route index element={<Dashboard tab="status" setUnread={setUnread} />} />
          <Route path="contract" element={<Dashboard tab="contract" setUnread={setUnread} />} />
          <Route path="invoice" element={<Dashboard tab="invoice" setUnread={setUnread} />} />
          <Route path="notifications" element={<Dashboard tab="notifications" setUnread={setUnread} />} />
        </Routes>
      </div>
    </div>
  );
}
